const LOCKED_SURFACE_SELECTOR = [
  'dialog[scroll-lock][open]',
  'details[scroll-lock][open]',
  "[data-header-search][scroll-lock][data-search-open='true']",
  "[data-header-search][scroll-lock][data-search-closing='true']",
].join(', ');
const VISUAL_VIEWPORT_SELECTOR = [
  'dialog[scroll-lock][data-modal-visual-viewport][open]',
  "[data-header-search][scroll-lock][data-modal-visual-viewport][data-search-open='true']",
  "[data-header-search][scroll-lock][data-modal-visual-viewport][data-search-closing='true']",
].join(', ');
const VISUAL_VIEWPORT_SURFACE_SELECTOR = '[scroll-lock][data-modal-visual-viewport]';
const DOCUMENT_SCROLL_MEDIA = '(max-width: 959px), (max-height: 699px)';
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
const visualViewport = window.visualViewport;
const surfaceViewportHeights = new WeakMap();

let lockedScrollX = 0;
let lockedScrollY = 0;
let lockedLayoutHeight = 0;
let lockedLayoutWidth = 0;
let restingBodyWidth = 0;
let lockedScrollbarWidth = 0;
let fixedBodyLock = false;
let previousBodyTop = '';
let previousBodyWidth = '';
let viewportFrameId;
let focusFrameId;
let scrollRestoreFrameId;
let viewportSettleTimers = [];
let documentScrollSurface = null;
let documentReturnScrollX = 0;
let documentReturnScrollY = 0;
let documentSurfaceResizeObserver = null;
const documentBackgroundElements = new Set();
const documentBackgroundInertElements = new Set();

const dispatchScrollRestoreEvent = (name) => {
  if (typeof CustomEvent !== 'function') return;
  document.dispatchEvent?.(new CustomEvent(name));
};

const finishScrollRestoration = () => {
  window.cancelAnimationFrame(scrollRestoreFrameId);
  scrollRestoreFrameId = undefined;

  if (!document.body.classList.contains('modal-scroll-restoring')) return;

  document.body.classList.remove('modal-scroll-restoring');
  dispatchScrollRestoreEvent('sigma:modal-scroll-restored');
};

const openLockedSurfaces = () => [...document.querySelectorAll(LOCKED_SURFACE_SELECTOR)]
  .filter((surface) => surface !== documentScrollSurface);

const syncDocumentSurfaceHeight = () => {
  if (!documentScrollSurface) return;

  // Retain the page's scroll range when a nested dialog temporarily fixes body.
  const flowRoot = documentScrollSurface.closest('[data-modal-document-flow-root]')
    || documentScrollSurface;
  document.documentElement.style.setProperty(
    '--modal-document-height',
    `${Math.ceil(flowRoot.getBoundingClientRect().height)}px`,
  );
};

const restoreDocumentBackground = () => {
  documentBackgroundElements.forEach((element) => {
    element.removeAttribute('data-modal-document-background');
  });
  documentBackgroundElements.clear();
  documentBackgroundInertElements.forEach((element) => element.removeAttribute('inert'));
  documentBackgroundInertElements.clear();
};

const syncDocumentBackground = () => {
  restoreDocumentBackground();
  if (!documentScrollSurface) return;

  const openSurfaces = [...document.querySelectorAll(LOCKED_SURFACE_SELECTOR)];
  [...document.body.children].forEach((element) => {
    if (!(element instanceof HTMLElement) || element.matches('script, style, link, dialog')) return;
    if (element.contains(documentScrollSurface) || openSurfaces.some((surface) => element.contains(surface))) return;

    // The compact surface is document content, so its background must be both
    // out of the flow and inert, including fixed page controls.
    element.setAttribute('data-modal-document-background', '');
    documentBackgroundElements.add(element);
    if (!element.hasAttribute('inert')) {
      element.setAttribute('inert', '');
      documentBackgroundInertElements.add(element);
    }
  });
};

const syncDocumentScrollSurface = () => {
  const nextSurface = [...document.querySelectorAll(LOCKED_SURFACE_SELECTOR)]
    .find((surface) => surface.hasAttribute?.('data-modal-document-scroll')
      && window.matchMedia?.(surface.dataset.modalDocumentScrollMedia || DOCUMENT_SCROLL_MEDIA).matches)
    || null;

  if (nextSurface === documentScrollSurface) {
    syncDocumentBackground();
    return;
  }

  if (documentScrollSurface) {
    const previousSurface = documentScrollSurface;
    const surfaceScrollY = window.scrollY;
    const focusedControl = document.activeElement;
    documentSurfaceResizeObserver?.disconnect();
    documentSurfaceResizeObserver = null;
    previousSurface.setAttribute('data-modal-document-scroll', '');
    if (previousSurface instanceof HTMLDialogElement) {
      previousSurface.removeAttribute('aria-modal');
    }
    documentScrollSurface = null;
    restoreDocumentBackground();
    document.body.classList.remove('modal-document-scrolling');
    document.documentElement.style.removeProperty('--modal-document-height');
    window.scrollTo({ left: documentReturnScrollX, top: documentReturnScrollY, behavior: 'instant' });
    if (previousSurface.open) {
      if (previousSurface instanceof HTMLDialogElement && !previousSurface.matches(':modal')) {
        previousSurface.close();
        previousSurface.showModal();
      }
      previousSurface.scrollTop = surfaceScrollY;
      if (previousSurface.contains(focusedControl)) focusedControl.focus({ preventScroll: true });
    }
  }

  if (!nextSurface) return;

  const surfaceScrollY = nextSurface.scrollTop;
  // Restore the shopping page before replacing a desktop/body scroll lock.
  unlockPageScroll();
  documentReturnScrollX = window.scrollX;
  documentReturnScrollY = window.scrollY;
  documentScrollSurface = nextSurface;
  const focusedControl = document.activeElement;
  if (nextSurface instanceof HTMLDialogElement) {
    // Top-layer dialogs are viewport-clipped by Safari. A full-screen compact
    // bag uses the ARIA modal pattern in normal document flow instead.
    if (nextSurface.matches(':modal')) {
      nextSurface.close();
      nextSurface.show();
    }
    nextSurface.setAttribute('aria-modal', 'true');
  }
  document.body.classList.add('modal-document-scrolling');
  nextSurface.setAttribute('data-modal-document-scroll', 'active');
  nextSurface.scrollTop = 0;
  syncDocumentBackground();
  syncDocumentSurfaceHeight();
  window.scrollTo({ left: 0, top: surfaceScrollY, behavior: 'instant' });
  if (nextSurface.contains(focusedControl)) focusedControl.focus({ preventScroll: true });

  if (typeof ResizeObserver === 'function') {
    documentSurfaceResizeObserver = new ResizeObserver(syncDocumentSurfaceHeight);
    documentSurfaceResizeObserver.observe(
      nextSurface.closest('[data-modal-document-flow-root]') || nextSurface,
    );
  }
};

const documentFocusables = () => [...documentScrollSurface.querySelectorAll(FOCUSABLE_SELECTOR)]
  .filter((element) => element instanceof HTMLElement
    && element.tabIndex >= 0
    && element.getAttribute('aria-disabled') !== 'true'
    && element.getAttribute('aria-hidden') !== 'true'
    && !element.closest('[inert]')
    && element.getClientRects().length > 0);

const containDocumentFocus = (event) => {
  if (event.defaultPrevented || event.key !== 'Tab' || !documentScrollSurface
    || openLockedSurfaces().length) return;
  const focusables = documentFocusables();
  const first = focusables[0];
  const last = focusables.at(-1);
  if (!first || !last) return;
  const focusedControl = document.activeElement;
  if (event.shiftKey && (focusedControl === first || !documentScrollSurface.contains(focusedControl))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (focusedControl === last || !documentScrollSurface.contains(focusedControl))) {
    event.preventDefault();
    first.focus();
  }
};

const getLockedBodyWidth = () => {
  const compactScrollbarsAreHidden =
    window.matchMedia?.('(max-width: 1023px)').matches ?? window.innerWidth <= 1023;
  const reservedScrollbarWidth = compactScrollbarsAreHidden ? 0 : lockedScrollbarWidth;

  return Math.max(0, window.innerWidth - reservedScrollbarWidth);
};

const shouldFixBodyForScrollLock = () =>
  window.matchMedia?.('(max-width: 1023px)').matches ?? window.innerWidth <= 1023;

const canConsumeWheelDelta = (target, deltaX, deltaY) => {
  const surface = target?.closest?.(LOCKED_SURFACE_SELECTOR);
  if (!surface) return false;

  let element = target;
  while (element) {
    const styles = window.getComputedStyle?.(element);
    const canScrollY =
      /auto|scroll|overlay/.test(styles?.overflowY || '') &&
      element.scrollHeight > element.clientHeight &&
      (deltaY < 0
        ? element.scrollTop > 0
        : element.scrollTop + element.clientHeight < element.scrollHeight);
    const canScrollX =
      /auto|scroll|overlay/.test(styles?.overflowX || '') &&
      element.scrollWidth > element.clientWidth &&
      (deltaX < 0
        ? element.scrollLeft > 0
        : element.scrollLeft + element.clientWidth < element.scrollWidth);

    if (canScrollY || canScrollX) return true;
    if (element === surface) break;
    element = element.parentElement;
  }

  return false;
};

const preventDesktopPageWheel = (event) => {
  if (!document.body.classList.contains('modal-scroll-locked') || fixedBodyLock) return;
  if (canConsumeWheelDelta(event.target, event.deltaX, event.deltaY)) return;

  event.preventDefault();
};

const keepFocusedControlVisible = () => {
  const focusedControl = document.activeElement;
  if (!(focusedControl instanceof HTMLElement)) return;

  const scrollport = focusedControl.closest?.('[data-modal-keyboard-scroll]');
  const surface = scrollport?.closest?.(VISUAL_VIEWPORT_SURFACE_SELECTOR);
  if (!scrollport || surface?.dataset.modalKeyboard !== 'true') return;

  const viewportTop = visualViewport?.offsetTop || 0;
  const viewportBottom = viewportTop + (visualViewport?.height || window.innerHeight);
  const scrollportBounds = scrollport.getBoundingClientRect();
  const focusedBounds = focusedControl.getBoundingClientRect();
  const edgePadding = 16;
  const visibleTop = Math.max(scrollportBounds.top + edgePadding, viewportTop + edgePadding);
  const visibleBottom = Math.min(
    scrollportBounds.bottom - edgePadding,
    viewportBottom - edgePadding,
  );

  if (focusedBounds.bottom > visibleBottom) {
    if (surface === documentScrollSurface) {
      window.scrollBy(0, focusedBounds.bottom - visibleBottom);
    } else {
      scrollport.scrollTop += focusedBounds.bottom - visibleBottom;
    }
  } else if (focusedBounds.top < visibleTop) {
    if (surface === documentScrollSurface) {
      window.scrollBy(0, focusedBounds.top - visibleTop);
    } else {
      scrollport.scrollTop -= visibleTop - focusedBounds.top;
    }
  }
};

const scheduleFocusedControlSync = () => {
  window.cancelAnimationFrame(focusFrameId);
  focusFrameId = window.requestAnimationFrame(() => {
    focusFrameId = undefined;
    keepFocusedControlVisible();
  });
};

const lockPageScroll = () => {
  if (document.body.classList.contains('modal-scroll-locked')) {
    // CSS changes its lock mechanism at the compact breakpoint. Reconcile the
    // saved position in the resize event, before that change can be painted.
    const nextFixedBodyLock = shouldFixBodyForScrollLock();
    if (nextFixedBodyLock !== fixedBodyLock) {
      fixedBodyLock = nextFixedBodyLock;
      if (!fixedBodyLock) {
        lockedScrollbarWidth = Math.max(
          0,
          window.innerWidth - (document.documentElement.clientWidth || window.innerWidth),
        );
      }
      document.body.style.top = fixedBodyLock ? `${-lockedScrollY}px` : previousBodyTop;
      document.body.style.width = `${getLockedBodyWidth()}px`;
      if (!fixedBodyLock) window.scrollTo({ left: lockedScrollX, top: lockedScrollY, behavior: 'instant' });
    }
    return;
  }

  finishScrollRestoration();

  lockedScrollX = window.scrollX;
  lockedScrollY = window.scrollY;
  lockedLayoutHeight = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight,
    (visualViewport?.offsetTop || 0) + (visualViewport?.height || 0),
  );
  lockedLayoutWidth = document.documentElement.clientWidth || window.innerWidth;
  fixedBodyLock = shouldFixBodyForScrollLock();
  previousBodyTop = document.body.style.top;
  previousBodyWidth = document.body.style.width;

  const bodyWidth =
    restingBodyWidth ||
    document.body.getBoundingClientRect?.().width ||
    document.documentElement.clientWidth ||
    window.innerWidth;
  lockedScrollbarWidth = Math.max(0, window.innerWidth - bodyWidth);

  document.body.style.width = `${bodyWidth}px`;
  if (fixedBodyLock) {
    document.body.style.top = `${-lockedScrollY}px`;
  }
  document.body.classList.add('modal-scroll-locked');
};

const unlockPageScroll = () => {
  if (!document.body.classList.contains('modal-scroll-locked')) {
    return;
  }

  document.body.classList.add('modal-scroll-restoring');
  dispatchScrollRestoreEvent('sigma:modal-scroll-restoring');
  document.body.classList.remove('modal-scroll-locked');
  document.body.style.top = previousBodyTop;
  document.body.style.width = previousBodyWidth;
  if (fixedBodyLock) {
    window.scrollTo({ left: lockedScrollX, top: lockedScrollY, behavior: 'instant' });
  }
  lockedLayoutHeight = 0;
  lockedLayoutWidth = 0;
  lockedScrollbarWidth = 0;
  fixedBodyLock = false;

  window.cancelAnimationFrame(scrollRestoreFrameId);
  scrollRestoreFrameId = window.requestAnimationFrame(() => {
    scrollRestoreFrameId = window.requestAnimationFrame(finishScrollRestoration);
  });
};

const syncViewportMetrics = () => {
  const viewportHeight = Math.round(visualViewport?.height || window.innerHeight);
  const viewportTop = Math.max(0, Math.round(visualViewport?.offsetTop || 0));
  const currentLayoutHeight = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight,
    viewportTop + viewportHeight,
  );
  const currentLayoutWidth = document.documentElement.clientWidth || window.innerWidth;
  const pageScrollIsLocked = document.body.classList.contains('modal-scroll-locked');

  if (pageScrollIsLocked) {
    document.body.style.width = `${getLockedBodyWidth()}px`;
  } else {
    restingBodyWidth =
      document.body.getBoundingClientRect?.().width || currentLayoutWidth;
  }
  const focusedControl = document.activeElement;
  const hasFocusedTextControl =
    focusedControl instanceof HTMLElement &&
    focusedControl.matches(
      'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), textarea',
    );
  const keyboardWasOpen = Array.from(
    document.querySelectorAll(VISUAL_VIEWPORT_SURFACE_SELECTOR),
  ).some((surface) => surface.dataset.modalKeyboard === 'true');

  if (
    (!hasFocusedTextControl && !keyboardWasOpen) ||
    (lockedLayoutWidth && currentLayoutWidth !== lockedLayoutWidth)
  ) {
    lockedLayoutHeight = currentLayoutHeight;
    lockedLayoutWidth = currentLayoutWidth;
  }

  const layoutHeight = Math.max(currentLayoutHeight, lockedLayoutHeight);
  const viewportBottom = Math.max(0, layoutHeight - viewportTop - viewportHeight);
  let keyboardSurfaceIsOpen = false;

  document.querySelectorAll(VISUAL_VIEWPORT_SELECTOR).forEach((surface) => {
    const surfaceHasFocusedTextControl =
      hasFocusedTextControl && surface.contains(focusedControl);
    let restingViewportHeight = surfaceViewportHeights.get(surface);

    if (
      !restingViewportHeight ||
      (!surfaceHasFocusedTextControl && surface.dataset.modalKeyboard !== 'true')
    ) {
      restingViewportHeight = viewportHeight;
      surfaceViewportHeights.set(surface, restingViewportHeight);
    }

    const keyboardIsOpen =
      surfaceHasFocusedTextControl &&
      (restingViewportHeight - viewportHeight > 120 || layoutHeight - viewportHeight > 120);
    keyboardSurfaceIsOpen ||= keyboardIsOpen;

    surface.style.setProperty('--modal-viewport-top', `${viewportTop}px`);
    surface.style.setProperty('--modal-viewport-bottom', `${viewportBottom}px`);
    surface.style.setProperty('--modal-layout-viewport-height', `${layoutHeight}px`);
    surface.style.setProperty('--modal-visible-viewport-height', `${viewportHeight}px`);
    surface.dataset.modalKeyboard = String(keyboardIsOpen);

    if (typeof CustomEvent === 'function') {
      surface.dispatchEvent?.(
        new CustomEvent('sigma:modal-viewport-change', {
          detail: {
            keyboardOpen: keyboardIsOpen,
            viewportHeight,
            viewportTop,
            viewportBottom,
          },
        }),
      );
    }
  });

  syncDocumentSurfaceHeight();

  if (keyboardSurfaceIsOpen) {
    scheduleFocusedControlSync();
  }
};

const clearClosedViewportMetrics = () => {
  document.querySelectorAll(VISUAL_VIEWPORT_SURFACE_SELECTOR).forEach((surface) => {
    if (surface.matches(VISUAL_VIEWPORT_SELECTOR)) return;

    surfaceViewportHeights.delete(surface);
    surface.style.removeProperty('--modal-viewport-top');
    surface.style.removeProperty('--modal-viewport-bottom');
    surface.style.removeProperty('--modal-layout-viewport-height');
    surface.style.removeProperty('--modal-visible-viewport-height');
    delete surface.dataset.modalKeyboard;
  });
};

const syncPageScrollLock = () => {
  syncDocumentScrollSurface();
  if (openLockedSurfaces().length > 0) {
    lockPageScroll();
  } else {
    unlockPageScroll();
  }
};

const syncViewportState = () => {
  clearClosedViewportMetrics();
  syncViewportMetrics();
};

const syncModalState = () => {
  syncPageScrollLock();
  syncViewportState();
};

const scheduleSync = () => {
  // Preserve the page position before an opening surface can trigger a paint.
  // Viewport measurements remain frame-scheduled so resize events can coalesce.
  syncPageScrollLock();
  window.cancelAnimationFrame(viewportFrameId);
  viewportFrameId = window.requestAnimationFrame(() => {
    viewportFrameId = undefined;
    syncViewportState();
  });
};

const scheduleSettledSync = () => {
  viewportSettleTimers.forEach((timer) => window.clearTimeout(timer));
  viewportSettleTimers = [0, 120, 320].map((delay) =>
    window.setTimeout(scheduleSync, delay),
  );
};

// Opening and closing attributes drive visibility and enter/exit motion. Apply
// their geometry in the same pre-paint mutation checkpoint so an opening
// surface never animates from the CSS viewport fallback before snapping to the
// measured visual viewport. Continuous resize and scroll events stay
// frame-coalesced through scheduleSync.
new MutationObserver(syncModalState).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['open', 'data-search-open', 'data-search-closing'],
  subtree: true,
});

window.addEventListener('resize', scheduleSync);
window.addEventListener('orientationchange', scheduleSync);
visualViewport?.addEventListener('resize', scheduleSync);
visualViewport?.addEventListener('scroll', scheduleSync);
visualViewport?.addEventListener('scrollend', scheduleSettledSync);
document.addEventListener?.('focusin', scheduleSync);
document.addEventListener?.('focusout', scheduleSettledSync);
document.addEventListener?.('keydown', containDocumentFocus);
document.addEventListener?.('wheel', preventDesktopPageWheel, { passive: false });
syncModalState();
